import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Text, 
  Title, 
  Badge, 
  Group, 
  Stack, 
  Button, 
  Modal, 
  TextInput,
  Notification,
  Loader,
  Center
} from '@mantine/core';
import { 
  IconCalendar, 
  IconMapPin, 
  IconCheck, 
  IconStar, 
  IconX,
  IconMail,
  IconUser,
  IconPhone 
} from '@tabler/icons-react';
import { useSadhanaData } from '../hooks/useSadhanaData';
import { useRSVP } from '../hooks/useRSVP';
import './RSVPCard.css';

const RSVPCard = () => {
  const { sadhanaData, loading: sadhanaLoading } = useSadhanaData();
  const { createRSVP, loading: rsvpLoading, error, success, reset } = useRSVP();
  
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: ''
  });
  const [emailError, setEmailError] = useState('');
  const [notification, setNotification] = useState(null);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Handle RSVP button click
  const handleRSVPClick = (status) => {
    setSelectedStatus(status);
    setModalOpened(true);
    reset();
  };

  // Validate email
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate email
    if (!formData.email) {
      setEmailError('Email is required');
      return;
    }
    if (!validateEmail(formData.email)) {
      setEmailError('Please enter a valid email');
      return;
    }

    setEmailError('');

    // Get the latest Sadhana event ID
    const eventId = sadhanaData.id || sadhanaData.timestamp?.toString() || Date.now().toString();

    const rsvpData = {
      eventId,
      email: formData.email,
      name: formData.name,
      phone: formData.phone,
      status: selectedStatus,
      eventDate: sadhanaData.date,
      eventLocation: sadhanaData.location
    };

    const result = await createRSVP(rsvpData);

    if (result.success) {
      setModalOpened(false);
      setNotification({
        type: 'success',
        message: result.message
      });
      // Clear form
      setFormData({ email: '', name: '', phone: '' });
      
      // Clear notification after 5 seconds
      setTimeout(() => {
        setNotification(null);
      }, 5000);
    }
  };

  // Show notification when there's an error or success
  useEffect(() => {
    if (error) {
      setNotification({
        type: 'error',
        message: error
      });
    }
  }, [error]);

  if (sadhanaLoading) {
    return (
      <Center style={{ height: 200 }}>
        <Loader size="lg" color="yellow" />
      </Center>
    );
  }

  if (!sadhanaData.date || !sadhanaData.location) {
    return null;
  }

  const statusConfig = {
    attending: {
      label: 'Attending',
      icon: IconCheck,
      color: 'green',
      gradient: { from: 'teal', to: 'lime' }
    },
    interested: {
      label: 'Interested',
      icon: IconStar,
      color: 'yellow',
      gradient: { from: 'orange', to: 'yellow' }
    },
    cannot_attend: {
      label: 'Cannot Attend',
      icon: IconX,
      color: 'red',
      gradient: { from: 'pink', to: 'orange' }
    }
  };

  return (
    <>
      <Card 
        shadow="xl" 
        padding="xl" 
        radius="lg" 
        className="rsvp-card"
        withBorder
      >
        <div className="rsvp-card-header">
          <Badge 
            size="lg" 
            variant="gradient" 
            gradient={{ from: 'yellow', to: 'orange' }}
            className="rsvp-badge"
          >
            Next Sadhana
          </Badge>
        </div>

        <Stack spacing="md" mt="md">
          <Title order={2} className="rsvp-title">
            Amrit Vela Sadhana
          </Title>

          <Group spacing="lg">
            <Group spacing="xs">
              <IconCalendar size={20} className="rsvp-icon" />
              <Text size="md" weight={500}>
                {formatDate(sadhanaData.date)}
              </Text>
            </Group>
          </Group>

          <Group spacing="xs">
            <IconMapPin size={20} className="rsvp-icon" />
            <Text size="md" weight={500}>
              {sadhanaData.location}
            </Text>
          </Group>

          <Text size="sm" color="dimmed" mt="sm">
            Join us for a transformative 2.5 hour practice of meditation, movement, and kirtan starting at 4:00 AM.
          </Text>

          <div className="rsvp-divider" />

          <Stack spacing="xs">
            <Text size="sm" weight={500} align="center">
              RSVP to receive reminder emails
            </Text>
            <Text size="xs" color="dimmed" align="center">
              You'll receive a reminder 1 week before the Sadhana
            </Text>
          </Stack>

          <Group grow mt="md">
            {Object.entries(statusConfig).map(([status, config]) => {
              const Icon = config.icon;
              return (
                <Button
                  key={status}
                  variant="gradient"
                  gradient={config.gradient}
                  size="md"
                  radius="md"
                  leftIcon={<Icon size={18} />}
                  onClick={() => handleRSVPClick(status)}
                  className="rsvp-button"
                >
                  {config.label}
                </Button>
              );
            })}
          </Group>
        </Stack>
      </Card>

      {/* RSVP Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title={
          <Text size="lg" weight={600}>
            RSVP for Sadhana - {statusConfig[selectedStatus]?.label}
          </Text>
        }
        size="md"
        radius="md"
        centered
      >
        <Stack spacing="md">
          <TextInput
            label="Email"
            placeholder="your@email.com"
            icon={<IconMail size={16} />}
            required
            value={formData.email}
            onChange={(e) => {
              setFormData({ ...formData, email: e.target.value });
              setEmailError('');
            }}
            error={emailError}
          />

          <TextInput
            label="Name"
            placeholder="Your name (optional)"
            icon={<IconUser size={16} />}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <TextInput
            label="Phone"
            placeholder="Your phone (optional)"
            icon={<IconPhone size={16} />}
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />

          <Text size="xs" color="dimmed">
            By submitting, you'll receive a confirmation email immediately and a reminder email 1 week before the Sadhana with event details.
          </Text>

          <Group position="right" mt="md">
            <Button 
              variant="subtle" 
              onClick={() => setModalOpened(false)}
            >
              Cancel
            </Button>
            <Button
              variant="gradient"
              gradient={statusConfig[selectedStatus]?.gradient}
              onClick={handleSubmit}
              loading={rsvpLoading}
            >
              Submit RSVP
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Notification */}
      {notification && (
        <Notification
          icon={notification.type === 'success' ? <IconCheck size={18} /> : <IconX size={18} />}
          color={notification.type === 'success' ? 'green' : 'red'}
          title={notification.type === 'success' ? 'Success!' : 'Error'}
          onClose={() => setNotification(null)}
          className="rsvp-notification"
        >
          {notification.message}
        </Notification>
      )}
    </>
  );
};

export default RSVPCard;